<template>
    <QDialog
        v-model="dialogVisible"
    >
        <q-card style="width: 80vw; max-width: 900px; min-height: 300px; overflow-x: hidden;">
            <QBar class="bg-primary text-white">
                <div class="text-subtitle1 text-weight-medium">
                    {{ activityData.id }}
                </div>
                <QSpace />
                <QBtn
                    dense
                    flat
                    color="white"
                    icon="close"
                    @click="handleClickClose"
                />
            </QBar>

            <q-card-section class="row items-center q-pb-none">
                <q-toggle
                    v-model="useFilters"
                    :label="$t('visualization.popup.connections.useFilters')"
                    @update:model-value="fetchConnections"
                />
                <q-toggle
                    v-model="useVariants"
                    :label="$t('visualization.popup.connections.useVariants')"
                    @update:model-value="fetchConnections"
                />
            </q-card-section>

            <div
                v-if="!isLoading"
                class="row q-pa-sm no-horizontal-scroll"
            >
                <q-card-section class="col-6 q-pt-none no-horizontal-scroll">
                    <div class="row items-center q-pa-sm">
                        <p style="font-size: medium; font-weight: bold">
                            {{ $t('visualization.popup.connections.goFrom') }}
                        </p>
                    </div>
                    <QTable
                        ref="table"
                        flat
                        outlined
                        bordered
                        virtualScroll
                        :rows="goFromRows"
                        :columns="columns1"
                        :loading="isLoading"
                        :rowsPerPageOptions="rowsPerPageOptions"
                        separator="none"
                        rowKey="uuid"
                        binaryStateSort
                        :pagination="{ sortBy: 'frequency', descending: true }"
                        tableHeaderClass="bg-grey-2"
                        :tableStyle="{ tableLayout: 'fixed', width: '100%', overflowX: 'hidden' }"
                        :style="{ maxWidth: '100%' }"
                    >
                        <template #body-cell-arc="props">
                            <q-td :props="props">
                                <div class="ellipsis-tooltip">
                                    {{ props.row.name }}
                                    <q-tooltip
                                        anchor="top middle"
                                        self="bottom middle"
                                        :delay="400"
                                    >
                                        {{ props.row.name }}
                                    </q-tooltip>
                                </div>
                            </q-td>
                        </template>
                    </QTable>
                </q-card-section>

                <q-card-section class="col-6 q-pt-none no-horizontal-scroll">
                    <div class="row items-center q-pa-sm">
                        <p style="font-size: medium; font-weight: bold">
                            {{ $t('visualization.popup.connections.goTo') }}
                        </p>
                    </div>
                    <QTable
                        ref="table"
                        flat
                        outlined
                        bordered
                        virtualScroll
                        :rows="goToRows"
                        :columns="columns2"
                        :loading="isLoading"
                        :rowsPerPageOptions="rowsPerPageOptions"
                        separator="none"
                        rowKey="uuid"
                        :pagination="{ sortBy: 'frequency', descending: true }"
                        binaryStateSort
                        tableHeaderClass="bg-grey-2"
                        :tableStyle="{ tableLayout: 'fixed', width: '100%', overflowX: 'hidden' }"
                        :style="{ maxWidth: '100%' }"
                    >
                        <template #body-cell-arc="props">
                            <q-td :props="props">
                                <div class="ellipsis-tooltip">
                                    {{ props.row.name }}
                                    <q-tooltip
                                        anchor="top middle"
                                        self="bottom middle"
                                        :delay="400"
                                    >
                                        {{ props.row.name }}
                                    </q-tooltip>
                                </div>
                            </q-td>
                        </template>
                    </QTable>
                </q-card-section>
            </div>
            <q-card-section
                v-else
                class="row justify-center items-center"
            >
                <q-spinner
                    color="grey-6"
                    size="3em"
                    :thickness="2"
                />
            </q-card-section>
        </q-card>
    </QDialog>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import 'moment-duration-format'
import {
    Api, apiRequest, notifyError,
} from '@/api'
import { filtersStorageMixin } from '@/mixins'

const ROWS_PER_PAGE_OPTIONS = [10, 15, 30, 50, 75, 100]

export default {
    name: 'TablesPopUp',
    mixins: [filtersStorageMixin],
    props: {
        data: VueTypes.object,
        activityData: VueTypes.object,
        visible: VueTypes.bool,
    },
    emits: ['close'],
    data() {
        return {
            isLoading: false,
            useFilters: true,
            useVariants: true,
            rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
            connectionsData: undefined,
            allColumns: [
                {
                    name: 'arc',
                    label: this.$t('visualization.popup.connections.rows.arc'),
                    field: (row) => row.name,
                    align: 'center',
                    sortable: true,
                    style: 'max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;',
                    headerStyle: 'max-width: 200px;',
                },
                {
                    name: 'frequency',
                    label: this.$t('visualization.popup.connections.rows.frequency'),
                    field: (row) => row.frequency,
                    align: 'center',
                    sortable: true,
                },
                {
                    name: 'avgDuration',
                    label: this.$t('visualization.popup.connections.rows.avgDuration'),
                    field: (row) => row.avgDuration,
                    align: 'center',
                    sortable: true,
                    format: (val) => moment.duration(val, 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false }),
                },
            ],
            columns1: [],
            columns2: [],
            goFromRows: [],
            goToRows: [],
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.fetchConnections()
            }
        },
    },
    methods: {
        async fetchConnections() {
            this.isLoading = true
            const activityId = this.activityData.id
            const datasetUid = this.$route.params.processId
            const { visualizationFilters } = this
            const { filters, filterSets, operator } = this.getActiveFiltersApiParts(visualizationFilters)

            const params = {
                ...(this.useFilters ? { filters } : { filters: [] }),
                ...(this.useFilters ? { filterSets } : { filterSets: [] }),
                operator,
                ...(this.useVariants && this.data.variantId ? { variantUid: this.data.variantId } : {}),
                ...(this.useVariants && this.data.variants ? { variantCount: this.data.variants } : {}),
                ...(this.useVariants && this.data.loopId ? { loopUid: this.data.loopId } : {}),
            }
            apiRequest(Api().visualizations.connections({ datasetUid, activityId, params }))
                .then((data) => {
                    this.connectionsData = data
                    this.updateInternalData()
                })
                .catch(notifyError)
                .finally(() => {
                    this.isLoading = false
                })
        },
        updateInternalData() {
            const normalize = (rows) => {
                if (!rows || rows.length === 0) return []
                if (typeof rows[0] === 'object') return rows
                return rows.map((name) => ({ name }))
            }

            const from = normalize(this.connectionsData.origin)
            const to = normalize(this.connectionsData.destination)

            this.goFromRows = from
            this.goToRows = to

            // eslint-disable-next-line no-prototype-builtins
            const hasExtraFields = [...from, ...to].some((row) => row.hasOwnProperty('frequency') || row.hasOwnProperty('duration'))

            this.columns1 = hasExtraFields
                ? cloneDeep(this.allColumns)
                : cloneDeep(this.allColumns.filter((c) => c.name === 'arc'))
            this.columns2 = hasExtraFields
                ? cloneDeep(this.allColumns)
                : cloneDeep(this.allColumns.filter((c) => c.name === 'arc'))
        },
        handleClickClose() {
            this.$emit('close')
        },
    },
}
</script>

  <style scoped>
  .q-card,
  .q-card-section,
  .q-table__container {
    max-width: 100%;
    overflow-x: hidden;
  }

  .no-horizontal-scroll {
    overflow-x: hidden !important;
  }

  .ellipsis-tooltip {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  </style>
